import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@mui/material';
import { calculateDefocusBlur, selectFocusPlanes } from './focusBlur';
import { calculateFieldOfViewDiameterMm, type ObjectivePower, OBJECTIVES } from './magnification';
import { calculateLighting, type LightingSettings } from './lighting';
import type { SpecimenSlide } from './specimens';

interface Point { x: number; y: number }
interface FieldOfViewProps {
  slide: SpecimenSlide;
  objective: ObjectivePower;
  focusOffsetUm: number;
  lighting: LightingSettings;
  measuring: boolean;
  onMeasurement: (measurement: { distanceMicrometres: number; uncertaintyMicrometres: number } | null) => void;
  onSnapshot: (dataUrl: string) => void;
  stagePan: Point;
  onStagePanChange: (pan: Point) => void;
  oilImmersion: boolean;
}

const VIEW_SIZE = 600;
const VIEW_RADIUS = 274;

function hashSeed(text: string): number {
  return Array.from(text).reduce((seed, char) => ((seed * 31) + char.charCodeAt(0)) >>> 0, 7);
}

function seededRandom(seedValue: number): () => number {
  let seed = seedValue || 1;
  return () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 4294967296;
  };
}

function drawSpecimen(ctx: CanvasRenderingContext2D, slide: SpecimenSlide, objective: ObjectivePower): void {
  const random = seededRandom(hashSeed(slide.id));
  const size = 15 * (objective / 4);
  const count = Math.ceil(VIEW_SIZE / size) + 4;
  const start = -count * size / 2;
  const center = (value: number) => value + size / 2;

  if (slide.kind === 'calibration') {
    ctx.strokeStyle = '#374151';
    ctx.lineWidth = 2;
    for (let x = -VIEW_SIZE; x <= VIEW_SIZE; x += size) {
      ctx.beginPath(); ctx.moveTo(x, -VIEW_SIZE); ctx.lineTo(x, VIEW_SIZE); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(-VIEW_SIZE, x); ctx.lineTo(VIEW_SIZE, x); ctx.stroke();
    }
    ctx.lineWidth = 5;
    ctx.beginPath(); ctx.moveTo(-VIEW_SIZE, 0); ctx.lineTo(VIEW_SIZE, 0); ctx.stroke();
    ctx.fillStyle = '#10263b'; ctx.font = 'bold 18px sans-serif'; ctx.fillText('0', -VIEW_SIZE + 8, -12);
    return;
  }

  if (slide.kind === 'blood') {
    for (let index = 0; index < 115; index += 1) {
      const x = (random() - 0.5) * VIEW_SIZE * 1.5;
      const y = (random() - 0.5) * VIEW_SIZE * 1.5;
      const radius = size * (0.22 + random() * 0.12);
      ctx.beginPath(); ctx.ellipse(x, y, radius * 1.2, radius * 0.83, random() * 0.4, 0, Math.PI * 2);
      ctx.fillStyle = '#ef9a9b'; ctx.fill(); ctx.strokeStyle = '#b94f63'; ctx.lineWidth = Math.max(1, size * 0.025); ctx.stroke();
      ctx.beginPath(); ctx.ellipse(x, y, radius * 0.48, radius * 0.3, 0, 0, Math.PI * 2); ctx.fillStyle = '#f8c4c1'; ctx.fill();
    }
    [0, 1, 2].forEach((index) => {
      const x = (index - 1) * size * 2.2;
      const y = (index % 2 ? -1 : 1) * size * 1.4;
      const radius = size * 0.62;
      ctx.beginPath(); ctx.arc(x, y, radius, 0, Math.PI * 2); ctx.fillStyle = '#d7c4ed'; ctx.fill(); ctx.strokeStyle = '#6e4c9a'; ctx.lineWidth = 3; ctx.stroke();
      ctx.beginPath(); ctx.arc(x, y, radius * 0.46, 0, Math.PI * 2); ctx.fillStyle = '#665087'; ctx.fill();
    });
    return;
  }

  if (slide.kind === 'leaf') {
    ctx.fillStyle = '#b6d899'; ctx.fillRect(-VIEW_SIZE, -VIEW_SIZE * 0.48, VIEW_SIZE * 2, size * 0.45);
    ctx.fillStyle = '#86bd72'; ctx.fillRect(-VIEW_SIZE, -VIEW_SIZE * 0.4, VIEW_SIZE * 2, size * 1.15);
    ctx.fillStyle = '#d3e9bb'; ctx.fillRect(-VIEW_SIZE, VIEW_SIZE * 0.08, VIEW_SIZE * 2, size * 1.45);
    ctx.fillStyle = '#9fc781';
    for (let index = 0; index < 26; index += 1) {
      const x = (random() - 0.5) * VIEW_SIZE * 1.8; const y = (random() - 0.5) * VIEW_SIZE * 1.1 + size * 1.1;
      ctx.beginPath(); ctx.ellipse(x, y, size * 0.38, size * 0.22, random() * 2, 0, Math.PI * 2); ctx.fill();
    }
    ctx.fillStyle = '#5a8e54'; ctx.fillRect(-size * 0.36, -VIEW_SIZE, size * 0.72, VIEW_SIZE * 2);
    return;
  }

  if (slide.kind === 'stem') {
    ctx.beginPath(); ctx.arc(0, 0, VIEW_SIZE * 0.82, 0, Math.PI * 2); ctx.fillStyle = '#e2dab0'; ctx.fill();
    ctx.beginPath(); ctx.arc(0, 0, VIEW_SIZE * 0.7, 0, Math.PI * 2); ctx.fillStyle = '#c9dca5'; ctx.fill();
    const bundles = 14;
    for (let index = 0; index < bundles; index += 1) {
      const angle = (index / bundles) * Math.PI * 2; const x = Math.cos(angle) * VIEW_SIZE * 0.5; const y = Math.sin(angle) * VIEW_SIZE * 0.5;
      ctx.beginPath(); ctx.ellipse(x, y, size * 0.48, size * 0.95, angle, 0, Math.PI * 2); ctx.fillStyle = '#b96867'; ctx.fill();
      ctx.beginPath(); ctx.ellipse(x, y, size * 0.2, size * 0.58, angle, 0, Math.PI * 2); ctx.fillStyle = '#f2e9c7'; ctx.fill();
    }
    return;
  }

  if (slide.kind === 'mitosis' || slide.kind === 'plant') {
    for (let row = 0; row < count; row += 1) for (let col = 0; col < count; col += 1) {
      const x = start + col * size; const y = start + row * size;
      ctx.fillStyle = slide.kind === 'mitosis' ? '#f6e7dc' : '#e9edce';
      ctx.fillRect(x, y, size, size);
      ctx.strokeStyle = slide.kind === 'mitosis' ? '#9a6757' : '#5d8660'; ctx.lineWidth = Math.max(1, size * 0.055); ctx.strokeRect(x, y, size, size);
      if (slide.kind === 'mitosis' && random() > 0.68) {
        ctx.strokeStyle = '#5a426f'; ctx.lineWidth = Math.max(2, size * 0.045);
        for (let chromosome = 0; chromosome < 5; chromosome += 1) {
          const cx = x + size * (0.3 + random() * 0.4); const cy = y + size * (0.3 + random() * 0.4);
          ctx.beginPath(); ctx.moveTo(cx - size * 0.08, cy - size * 0.08); ctx.lineTo(cx + size * 0.08, cy + size * 0.08); ctx.moveTo(cx + size * 0.08, cy - size * 0.08); ctx.lineTo(cx - size * 0.08, cy + size * 0.08); ctx.stroke();
        }
      } else {
        ctx.beginPath(); ctx.ellipse(center(x), center(y), size * 0.16, size * 0.13, 0, 0, Math.PI * 2); ctx.fillStyle = '#526f8f'; ctx.fill();
        ctx.beginPath(); ctx.ellipse(center(x), center(y), size * 0.31, size * 0.35, 0, 0, Math.PI * 2); ctx.fillStyle = '#d8ddb8'; ctx.fill();
      }
    }
    return;
  }

  if (slide.kind === 'bacteria') {
    for (let index = 0; index < 190; index += 1) {
      const x = (random() - 0.5) * VIEW_SIZE * 1.6; const y = (random() - 0.5) * VIEW_SIZE * 1.6;
      const rod = random() > 0.45; const rotation = random() * Math.PI;
      ctx.save(); ctx.translate(x, y); ctx.rotate(rotation);
      ctx.fillStyle = random() > 0.5 ? '#663e9b' : '#d85c83';
      if (rod) { ctx.beginPath(); ctx.roundRect(-size * 0.22, -size * 0.07, size * 0.44, size * 0.14, size * 0.08); ctx.fill(); }
      else { ctx.beginPath(); ctx.arc(0, 0, size * 0.11, 0, Math.PI * 2); ctx.fill(); }
      ctx.restore();
    }
    return;
  }

  if (slide.kind === 'pond') {
    for (let index = 0; index < 13; index += 1) {
      const x = (random() - 0.5) * VIEW_SIZE * 1.4; const y = (random() - 0.5) * VIEW_SIZE * 1.4;
      const radius = size * (0.25 + random() * 0.35);
      ctx.beginPath(); ctx.ellipse(x, y, radius * 1.4, radius * 0.68, random() * 3, 0, Math.PI * 2); ctx.fillStyle = index % 2 ? '#9bd9bd' : '#a7d8e2'; ctx.fill(); ctx.strokeStyle = '#43888b'; ctx.lineWidth = 3; ctx.stroke();
      ctx.beginPath(); ctx.arc(x, y, radius * 0.18, 0, Math.PI * 2); ctx.fillStyle = '#326f78'; ctx.fill();
      for (let cilia = 0; cilia < 9; cilia += 1) { const a = (cilia / 9) * Math.PI * 2; ctx.beginPath(); ctx.moveTo(x + Math.cos(a) * radius, y + Math.sin(a) * radius * 0.5); ctx.lineTo(x + Math.cos(a) * radius * 1.15, y + Math.sin(a) * radius * 0.65); ctx.strokeStyle = '#528f91'; ctx.lineWidth = 2; ctx.stroke(); }
    }
    return;
  }

  if (slide.kind === 'yeast') {
    for (let index = 0; index < 80; index += 1) {
      const x = (random() - 0.5) * VIEW_SIZE * 1.5; const y = (random() - 0.5) * VIEW_SIZE * 1.5; const radius = size * (0.14 + random() * 0.08);
      ctx.beginPath(); ctx.arc(x, y, radius, 0, Math.PI * 2); ctx.fillStyle = '#f1e4bd'; ctx.fill(); ctx.strokeStyle = '#826b4f'; ctx.lineWidth = Math.max(2, size * 0.03); ctx.stroke();
      if (random() > 0.56) { ctx.beginPath(); ctx.arc(x + radius * 0.8, y - radius * 0.65, radius * 0.48, 0, Math.PI * 2); ctx.fillStyle = '#e3cf9e'; ctx.fill(); ctx.stroke(); }
    }
    return;
  }

  if (slide.kind === 'muscle') {
    for (let row = -count; row < count; row += 1) {
      const y = row * size * 0.4;
      ctx.fillStyle = row % 2 ? '#d993a4' : '#e6a5b1'; ctx.fillRect(-VIEW_SIZE, y, VIEW_SIZE * 2, size * 0.36);
      ctx.strokeStyle = '#a95870'; ctx.lineWidth = Math.max(1, size * 0.035);
      for (let x = -VIEW_SIZE; x < VIEW_SIZE; x += size * 0.22) { ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x, y + size * 0.36); ctx.stroke(); }
    }
    return;
  }

  if (slide.kind === 'nerve') {
    for (let index = 0; index < 8; index += 1) {
      const x = (random() - 0.5) * VIEW_SIZE; const y = (random() - 0.5) * VIEW_SIZE; const radius = size * 0.38;
      ctx.strokeStyle = '#806092'; ctx.lineWidth = Math.max(2, size * 0.06);
      for (let branch = 0; branch < 8; branch += 1) { const angle = (branch / 8) * Math.PI * 2; ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + Math.cos(angle) * size * 1.3, y + Math.sin(angle) * size * 1.3); ctx.stroke(); }
      ctx.beginPath(); ctx.arc(x, y, radius, 0, Math.PI * 2); ctx.fillStyle = '#d8c1dc'; ctx.fill(); ctx.stroke();
      ctx.beginPath(); ctx.arc(x, y, radius * 0.3, 0, Math.PI * 2); ctx.fillStyle = '#70518c'; ctx.fill();
    }
    return;
  }

  // Irregular epithelial cells represent cheek tissue.
  for (let row = 0; row < count; row += 1) for (let col = 0; col < count; col += 1) {
    const x = start + col * size; const y = start + row * size;
    ctx.beginPath();
    for (let point = 0; point < 8; point += 1) {
      const angle = (point / 8) * Math.PI * 2;
      const px = x + size * (0.5 + Math.cos(angle) * (0.47 + random() * 0.05));
      const py = y + size * (0.5 + Math.sin(angle) * (0.43 + random() * 0.05));
      if (point === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
    }
    ctx.closePath(); ctx.fillStyle = '#d6c9e7'; ctx.fill(); ctx.strokeStyle = '#79648e'; ctx.lineWidth = Math.max(1, size * 0.045); ctx.stroke();
    ctx.beginPath(); ctx.ellipse(center(x), center(y), size * 0.13, size * 0.17, 0, 0, Math.PI * 2); ctx.fillStyle = '#51416b'; ctx.fill();
  }
}

const FieldOfView: React.FC<FieldOfViewProps> = ({ slide, objective, focusOffsetUm, lighting, measuring, onMeasurement, onSnapshot, stagePan, onStagePanChange, oilImmersion }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dragOrigin = useRef<Point | null>(null);
  const imageCache = useRef<Map<string, HTMLImageElement>>(new Map());
  const [pan, setPan] = useState<Point>(stagePan);
  const [measurePoints, setMeasurePoints] = useState<Point[]>([]);
  const [loadedStackRevision, setLoadedStackRevision] = useState(0);
  const light = useMemo(() => calculateLighting(lighting), [lighting]);
  const focusStack = slide.imageStacks?.[objective];
  const fovDiameterMm = calculateFieldOfViewDiameterMm(18, objective);
  const specimenUmPerPixel = (fovDiameterMm * 1000) / (VIEW_RADIUS * 2);
  const blur = calculateDefocusBlur({ axialOffsetUm: focusOffsetUm, numericalAperture: OBJECTIVES[objective].numericalAperture });

  useEffect(() => {
    if (!focusStack?.length) return;
    let active = true;
    const pending = focusStack.filter((plane) => !imageCache.current.has(plane.src));
    if (pending.length === 0) return;
    pending.forEach((plane) => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        if (!active) return;
        imageCache.current.set(plane.src, image);
        setLoadedStackRevision((revision) => revision + 1);
      };
      image.onerror = () => {
        if (active) console.warn(`Could not load microscope focus plane: ${plane.src}`);
      };
      image.src = plane.src;
    });
    return () => { active = false; };
  }, [focusStack]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = VIEW_SIZE * ratio;
    canvas.height = VIEW_SIZE * ratio;
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.clearRect(0, 0, VIEW_SIZE, VIEW_SIZE);
    ctx.save();
    ctx.beginPath(); ctx.arc(VIEW_SIZE / 2, VIEW_SIZE / 2, VIEW_RADIUS, 0, Math.PI * 2); ctx.clip();
    ctx.fillStyle = slide.kind === 'calibration' ? '#f8f6eb' : slide.kind === 'blood' ? '#f8e6e4' : '#f0edcf';
    ctx.fillRect(0, 0, VIEW_SIZE, VIEW_SIZE);
    ctx.translate(VIEW_SIZE / 2 + pan.x, VIEW_SIZE / 2 + pan.y);
    if (objective === 100 && !oilImmersion) ctx.filter = 'blur(4px)';
    const availableStack = focusStack?.filter((plane) => imageCache.current.has(plane.src));
    if (focusStack?.length && availableStack?.length === focusStack.length) {
      const selection = selectFocusPlanes(focusStack, focusOffsetUm);
      const lowerImage = imageCache.current.get(selection.lower.src);
      const upperImage = imageCache.current.get(selection.upper.src);
      ctx.filter = blur.blurRadiusPx > 0.05 ? `blur(${Math.min(10, blur.blurRadiusPx)}px)` : 'none';
      const drawCover = (image: HTMLImageElement) => {
        const scale = Math.max(VIEW_SIZE / image.naturalWidth, VIEW_SIZE / image.naturalHeight);
        const width = VIEW_SIZE / scale; const height = VIEW_SIZE / scale;
        const sx = (image.naturalWidth - width) / 2; const sy = (image.naturalHeight - height) / 2;
        ctx.drawImage(image, sx, sy, width, height, -VIEW_SIZE / 2, -VIEW_SIZE / 2, VIEW_SIZE, VIEW_SIZE);
      };
      if (lowerImage) { ctx.globalAlpha = selection.lower === selection.upper ? 1 : 1 - selection.blend; drawCover(lowerImage); }
      if (upperImage && selection.upper !== selection.lower) { ctx.globalAlpha = selection.blend; drawCover(upperImage); }
      ctx.globalAlpha = 1;
    } else {
      ctx.filter = blur.blurRadiusPx > 0.05 ? `blur(${Math.min(10, blur.blurRadiusPx)}px)` : 'none';
      drawSpecimen(ctx, slide, objective);
    }
    ctx.filter = 'none';
    ctx.restore();

    // Lamp level drives transmitted illumination; low levels intentionally dim the field.
    ctx.save();
    ctx.beginPath(); ctx.arc(VIEW_SIZE / 2, VIEW_SIZE / 2, VIEW_RADIUS, 0, Math.PI * 2); ctx.clip();
    if (!light.isDark && light.illumination < 0.995) {
      ctx.fillStyle = `rgba(7, 14, 24, ${Math.min(0.88, 1 - light.illumination)})`;
      ctx.fillRect(0, 0, VIEW_SIZE, VIEW_SIZE);
    } else if (light.isDark) {
      ctx.fillStyle = 'rgba(4, 7, 11, 0.97)'; ctx.fillRect(0, 0, VIEW_SIZE, VIEW_SIZE);
    }
    const vignette = ctx.createRadialGradient(VIEW_SIZE / 2, VIEW_SIZE / 2, VIEW_RADIUS * 0.58, VIEW_SIZE / 2, VIEW_SIZE / 2, VIEW_RADIUS);
    vignette.addColorStop(0, 'rgba(0,0,0,0)'); vignette.addColorStop(1, `rgba(8,12,18,${0.18 + objective / 500})`);
    ctx.fillStyle = vignette; ctx.fillRect(0, 0, VIEW_SIZE, VIEW_SIZE);
    const scaleLengthUm = objective === 100 ? 5 : objective === 40 ? 10 : objective === 10 ? 50 : 100;
    const scaleLengthPx = Math.min(150, scaleLengthUm / specimenUmPerPixel);
    ctx.strokeStyle = '#fff'; ctx.fillStyle = '#fff'; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(VIEW_SIZE / 2 - scaleLengthPx / 2, VIEW_SIZE - 64); ctx.lineTo(VIEW_SIZE / 2 + scaleLengthPx / 2, VIEW_SIZE - 64); ctx.stroke();
    ctx.font = 'bold 14px sans-serif'; ctx.textAlign = 'center'; ctx.fillText(`${scaleLengthUm} µm`, VIEW_SIZE / 2, VIEW_SIZE - 72);
    ctx.restore();
  }, [slide, objective, focusOffsetUm, focusStack, loadedStackRevision, blur.blurRadiusPx, light, pan, oilImmersion, specimenUmPerPixel]);

  useEffect(() => {
    setMeasurePoints([]);
    onMeasurement(null);
  }, [slide.id, objective, onMeasurement]);

  const pointFromEvent = (event: React.PointerEvent<HTMLCanvasElement>): Point => {
    const rect = event.currentTarget.getBoundingClientRect();
    const scale = VIEW_SIZE / rect.width;
    return { x: (event.clientX - rect.left) * scale, y: (event.clientY - rect.top) * scale };
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    const point = pointFromEvent(event);
    if (measuring) {
      const next = measurePoints.length === 2 ? [point] : [...measurePoints, point];
      setMeasurePoints(next);
      if (next.length === 2) {
        const pixelDistance = Math.hypot(next[1].x - next[0].x, next[1].y - next[0].y);
        const distanceMicrometres = pixelDistance * specimenUmPerPixel;
        onMeasurement({ distanceMicrometres, uncertaintyMicrometres: Math.hypot(0.5, distanceMicrometres * 0.01) });
      } else onMeasurement(null);
      return;
    }
    dragOrigin.current = { x: point.x - pan.x, y: point.y - pan.y };
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!dragOrigin.current || measuring) return;
    const point = pointFromEvent(event);
    const next = { x: point.x - dragOrigin.current.x, y: point.y - dragOrigin.current.y };
    setPan(next);
    onStagePanChange(next);
  };

  const handlePointerUp = () => { dragOrigin.current = null; };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLCanvasElement>) => {
    const step = event.shiftKey ? 24 : 8;
    const next = { ...pan };
    if (event.key === 'ArrowLeft') next.x += step;
    else if (event.key === 'ArrowRight') next.x -= step;
    else if (event.key === 'ArrowUp') next.y += step;
    else if (event.key === 'ArrowDown') next.y -= step;
    else return;
    event.preventDefault(); setPan(next); onStagePanChange(next);
  };

  const captureSnapshot = () => {
    const source = canvasRef.current;
    if (!source) return;
    const snapshot = document.createElement('canvas');
    snapshot.width = VIEW_SIZE; snapshot.height = VIEW_SIZE + 64;
    const context = snapshot.getContext('2d');
    if (!context) return;
    context.fillStyle = '#111923'; context.fillRect(0, 0, snapshot.width, snapshot.height);
    context.drawImage(source, 0, 0, VIEW_SIZE, VIEW_SIZE);
    context.fillStyle = '#fff'; context.font = '14px sans-serif'; context.textAlign = 'left';
    context.fillText(`${slide.name} · ${objective * 10}× · ${new Date().toLocaleString()}`, 14, VIEW_SIZE + 24);
    context.fillText(`Lamp ${Math.round(lighting.lamp * 100)}% · Iris ${Math.round(lighting.diaphragm * 100)}%`, 14, VIEW_SIZE + 46);
    onSnapshot(snapshot.toDataURL('image/jpeg', 0.88));
  };

  return (
    <div className="field-view-wrap">
      <div className="eyepiece-shell">
        <canvas
          ref={canvasRef}
          className={`eyepiece-canvas ${measuring ? 'is-measuring' : ''}`}
          aria-label={`${slide.name} microscope field of view at ${objective * 10} times total magnification. ${slide.expectedObservation}${focusStack?.length ? ' Focus-stack imagery loaded.' : ' Educational schematic rendering; focal-stack imagery can be added by an administrator.'}`}
          role="img"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onKeyDown={handleKeyDown}
          tabIndex={0}
        />
        <svg className="measurement-overlay" viewBox={`0 0 ${VIEW_SIZE} ${VIEW_SIZE}`} aria-hidden="true">
          {measurePoints.length > 0 && <circle cx={measurePoints[0].x} cy={measurePoints[0].y} r="7" />}
          {measurePoints.length === 2 && <><line x1={measurePoints[0].x} y1={measurePoints[0].y} x2={measurePoints[1].x} y2={measurePoints[1].y} /><circle cx={measurePoints[1].x} cy={measurePoints[1].y} r="7" /></>}
          <line className="fov-scale" x1="222" y1="536" x2="378" y2="536" />
        </svg>
        <div className="field-label">{objective * 10}× · {fovDiameterMm.toFixed(2)} mm field</div>
        {blur.blurRadiusPx > 1.2 && <div className="focus-warning">Image out of focus · adjust fine focus</div>}
        {light.isDark && <div className="field-dark-label">Illuminator is off</div>}
      </div>
      <div className="field-caption"><span>Specimen: {slide.name}{focusStack?.length ? ' · focal stack' : ' · schematic preview'}</span><span>{measuring ? 'Click two points to measure' : 'Drag to pan the slide'}</span></div>
      <Button className="capture-snapshot-button" size="small" variant="outlined" onClick={captureSnapshot}>Capture eyepiece snapshot</Button>
    </div>
  );
};

export default FieldOfView;
